import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRoles } from '@/hooks/auth/useRoles';

export type ModuleType =
  | 'concursos'
  | 'enem'
  | 'oab'
  | 'videoaulas'
  | 'vestibulares'
  | 'juridicas'
  | 'residencia'
  | 'militares'
  | 'cnu'
  | 'bancario'
  | 'idiomas'
  | 'colegios-militares'
  | 'colegios'
  | 'reforco-escolar'
  | 'tribunais'
  | 'carreiras-policiais'
  | 'carreiras-juridicas';

interface ModuleAccessData {
  hasAccess: boolean;
  plan: string;
  loading: boolean;
  subscription: any | null;
}

// Admins/Developers/Teachers bypass all module gates.
// Reaproveita o cache `['user-roles', userId]` consolidado em useRoles().
function useStaffBypass() {
  const { isStaff, loading } = useRoles();
  return { isStaff, loading };
}


export function useModuleAccess(module: ModuleType): ModuleAccessData {
  const { user } = useAuth();
  const { isStaff, loading: staffLoading } = useStaffBypass();

  const { data, isLoading } = useQuery({
    queryKey: ['module-access', user?.id, module],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('module_subscriptions' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('module', module)
        .eq('status', 'active')
        .maybeSingle();

      if (!data) return null;
      if ((data as any).expires_at && new Date((data as any).expires_at) < new Date()) return null;
      return data;
    },
    enabled: !!user && !isStaff,
    staleTime: 60000,
  });

  if (isStaff) {
    return { hasAccess: true, plan: 'staff', loading: staffLoading, subscription: null };
  }

  return {
    hasAccess: !!data,
    plan: (data as any)?.plan || 'free',
    loading: isLoading || staffLoading,
    subscription: data,
  };
}

export function useAllModuleAccess() {
  const { user } = useAuth();
  const { isStaff, loading: staffLoading } = useStaffBypass();

  const { data, isLoading } = useQuery({
    queryKey: ['all-module-access', user?.id],
    queryFn: async () => {
      if (!user) return {};
      const { data } = await supabase
        .from('module_subscriptions' as any)
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active');

      const result: Record<string, any> = {};
      ((data as any[]) || []).forEach((sub: any) => {
        if (!sub.expires_at || new Date(sub.expires_at) > new Date()) {
          result[sub.module] = sub;
        }
      });
      return result;
    },
    enabled: !!user && !isStaff,
    staleTime: 60000,
  });

  return {
    modules: data || {},
    hasAccess: (_module: ModuleType) => isStaff || !!(data || {})[_module],
    loading: isLoading || staffLoading,
  };
}
